import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import 'react-datepicker/dist/react-datepicker.min.css';
import dayjs from 'dayjs';

export default function Reports() {
  const [activeTab, setActiveTab] = useState('tickets');
  const [dateFrom, setDateFrom] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [dateTo, setDateTo] = useState(new Date());
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedAirline, setSelectedAirline] = useState(null);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [searchCode, setSearchCode] = useState('');
  const [searchPassenger, setSearchPassenger] = useState('');
  const [budgetType, setBudgetType] = useState('');

  const formatDate = (date) => dayjs(date).format('YYYY-MM-DD');

  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => { const { data } = await api.get('/clients'); return data.data || data; },
  });

  const { data: suppliers } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => { const { data } = await api.get('/suppliers'); return data.data || data; },
  });

  const { data: airlines } = useQuery({
    queryKey: ['airlines'],
    queryFn: async () => { const { data } = await api.get('/airlines'); return data.data || data; },
  });

  const { data: destinations } = useQuery({
    queryKey: ['destinations'],
    queryFn: async () => { const { data } = await api.get('/destinations'); return data.data || data; },
  });

  // Ticket Search Report
  const { data: ticketSearchReport, isLoading: loadingTicketSearch, refetch: refetchTicketSearch } = useQuery({
    queryKey: ['report-ticket-search', formatDate(dateFrom), formatDate(dateTo), searchCode, searchPassenger, selectedClient?.value, selectedAirline?.value, selectedDestination?.value],
    queryFn: async () => {
      const params = new URLSearchParams({ date_from: formatDate(dateFrom), date_to: formatDate(dateTo) });
      if (searchCode) params.append('ticket_code', searchCode);
      if (searchPassenger) params.append('passenger_name', searchPassenger);
      if (selectedClient?.value) params.append('client_id', selectedClient.value);
      if (selectedAirline?.value) params.append('airline_id', selectedAirline.value);
      if (selectedDestination?.value) params.append('destination_id', selectedDestination.value);
      const { data } = await api.get(`/reports/ticket-search?${params}`);
      return data;
    },
    enabled: activeTab === 'tickets',
  });

  // Budget Report
  const { data: budgetReport, isLoading: loadingBudget, refetch: refetchBudget } = useQuery({
    queryKey: ['report-budget', formatDate(dateFrom), formatDate(dateTo), budgetType, selectedClient?.value, selectedSupplier?.value],
    queryFn: async () => {
      const params = new URLSearchParams({ date_from: formatDate(dateFrom), date_to: formatDate(dateTo) });
      if (budgetType) params.append('type', budgetType);
      if (budgetType === 'client' && selectedClient?.value) params.append('client_id', selectedClient.value);
      if (budgetType === 'supplier' && selectedSupplier?.value) params.append('supplier_id', selectedSupplier.value);
      const { data } = await api.get(`/reports/budget?${params}`);
      return data;
    },
    enabled: activeTab === 'budget',
  });

  // Client Report
  const { data: clientReport, isLoading: loadingClient, refetch: refetchClient } = useQuery({
    queryKey: ['report-client', selectedClient?.value, formatDate(dateFrom), formatDate(dateTo)],
    queryFn: async () => {
      const params = new URLSearchParams({ date_from: formatDate(dateFrom), date_to: formatDate(dateTo) });
      if (selectedClient?.value) params.append('client_id', selectedClient.value);
      const { data } = await api.get(`/reports/client-detail?${params}`);
      return data;
    },
    enabled: activeTab === 'client',
  });

  // Supplier Report
  const { data: supplierReport, isLoading: loadingSupplier, refetch: refetchSupplier } = useQuery({
    queryKey: ['report-supplier', selectedSupplier?.value, formatDate(dateFrom), formatDate(dateTo)],
    queryFn: async () => {
      const params = new URLSearchParams({ date_from: formatDate(dateFrom), date_to: formatDate(dateTo) });
      if (selectedSupplier?.value) params.append('supplier_id', selectedSupplier.value);
      const { data } = await api.get(`/reports/supplier-detail?${params}`);
      return data;
    },
    enabled: activeTab === 'supplier',
  });

  // Airline Report
  const { data: airlineReport, isLoading: loadingAirline, refetch: refetchAirline } = useQuery({
    queryKey: ['report-airline', selectedAirline?.value, formatDate(dateFrom), formatDate(dateTo)],
    queryFn: async () => {
      const params = new URLSearchParams({ date_from: formatDate(dateFrom), date_to: formatDate(dateTo) });
      if (selectedAirline?.value) params.append('airline_id', selectedAirline.value);
      const { data } = await api.get(`/reports/airline-detail?${params}`);
      return data;
    },
    enabled: activeTab === 'airline',
  });

  // Profit Report
  const { data: profitReport, isLoading: loadingProfit } = useQuery({
    queryKey: ['report-profit', formatDate(dateFrom), formatDate(dateTo)],
    queryFn: async () => {
      const { data } = await api.get(`/reports/profit?date_from=${formatDate(dateFrom)}&date_to=${formatDate(dateTo)}`);
      return data;
    },
    enabled: activeTab === 'profit',
  });

  const clientOptions = (clients || []).map(c => ({ value: c.id, label: `${c.code} - ${c.name}` }));
  const supplierOptions = (suppliers || []).map(s => ({ value: s.id, label: `${s.code} - ${s.name}` }));
  const airlineOptions = (airlines || []).map(a => ({ value: a.id, label: a.name }));
  const destinationOptions = (destinations || []).map(d => ({ value: d.id, label: d.name }));

  const tabs = [
    { id: 'tickets', name: 'Biletat' },
    { id: 'budget', name: 'Buxheti' },
    { id: 'client', name: 'Klienti' },
    { id: 'supplier', name: 'Furnitori' },
    { id: 'airline', name: 'Kompania' },
    { id: 'profit', name: 'Fitimi' },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Raporte</h1>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex border-b overflow-x-auto">
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex-shrink-0 px-6 py-4 font-medium transition-colors ${activeTab === tab.id ? 'bg-primary-50 text-primary-700 border-b-2 border-primary-600' : 'text-gray-600 hover:bg-gray-50'}`}>
              {tab.name}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* ===== RAPORT BILETASH ===== */}
          {activeTab === 'tickets' && (
            <div>
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Nga Data</label>
                    <DatePicker selected={dateFrom} onChange={setDateFrom} dateFormat="dd/MM/yyyy" className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Deri</label>
                    <DatePicker selected={dateTo} onChange={setDateTo} dateFormat="dd/MM/yyyy" className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Kodi Biletes</label>
                    <input type="text" value={searchCode} onChange={(e) => setSearchCode(e.target.value)} placeholder="Kerko kod..." className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Pasagjeri</label>
                    <input type="text" value={searchPassenger} onChange={(e) => setSearchPassenger(e.target.value)} placeholder="Kerko emer..." className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Klienti</label>
                    <Select value={selectedClient} onChange={setSelectedClient} options={clientOptions} isClearable placeholder="Te gjithe..." className="text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Kompania</label>
                    <Select value={selectedAirline} onChange={setSelectedAirline} options={airlineOptions} isClearable placeholder="Te gjitha..." className="text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Destinacioni</label>
                    <Select value={selectedDestination} onChange={setSelectedDestination} options={destinationOptions} isClearable placeholder="Te gjitha..." className="text-sm" />
                  </div>
                  <div className="flex items-end">
                    <button onClick={() => refetchTicketSearch()} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">Kerko</button>
                  </div>
                  <div className="flex items-end">
                    <button onClick={() => { setSearchCode(''); setSearchPassenger(''); setSelectedClient(null); setSelectedAirline(null); setSelectedDestination(null); }} className="w-full px-4 py-2 bg-gray-400 text-white rounded-lg font-medium hover:bg-gray-500">Pastro</button>
                  </div>
                </div>
              </div>

              {loadingTicketSearch ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>
              ) : ticketSearchReport ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg"><p className="text-xs text-blue-600 font-medium">Bileta</p><p className="text-2xl font-bold text-blue-700">{ticketSearchReport.totals?.count || 0}</p></div>
                    <div className="bg-green-50 p-4 rounded-lg"><p className="text-xs text-green-600 font-medium">Shitje</p><p className="text-2xl font-bold text-green-700">�{(ticketSearchReport.totals?.total || 0).toFixed(2)}</p></div>
                    <div className="bg-purple-50 p-4 rounded-lg"><p className="text-xs text-purple-600 font-medium">Tarifa</p><p className="text-2xl font-bold text-purple-700">�{(ticketSearchReport.totals?.fare || 0).toFixed(2)}</p></div>
                    <div className="bg-orange-50 p-4 rounded-lg"><p className="text-xs text-orange-600 font-medium">TSC</p><p className="text-2xl font-bold text-orange-700">�{(ticketSearchReport.totals?.tax || 0).toFixed(2)}</p></div>
                    <div className="bg-teal-50 p-4 rounded-lg"><p className="text-xs text-teal-600 font-medium">Cash</p><p className="text-2xl font-bold text-teal-700">�{(ticketSearchReport.totals?.cash || 0).toFixed(2)}</p></div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-blue-600 text-white">
                        <tr>
                          <th className="text-left py-3 px-3">Data</th>
                          <th className="text-left py-3 px-3">Kodi</th>
                          <th className="text-left py-3 px-3">Pasagjeri</th>
                          <th className="text-left py-3 px-3">Klienti</th>
                          <th className="text-left py-3 px-3">Destinacioni</th>
                          <th className="text-left py-3 px-3">Kompania</th>
                          <th className="text-right py-3 px-3">Tarifa</th>
                          <th className="text-right py-3 px-3">TSC</th>
                          <th className="text-right py-3 px-3">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ticketSearchReport.tickets?.length === 0 ? (
                          <tr><td colSpan="9" className="text-center py-8 text-gray-500">Nuk ka bileta</td></tr>
                        ) : (
                          ticketSearchReport.tickets?.map((t) => (
                            <tr key={t.id} className="hover:bg-gray-50 border-b">
                              <td className="py-2 px-3">{dayjs(t.ticket_date).format('DD/MM/YYYY')}</td>
                              <td className="py-2 px-3 font-mono text-xs">{t.ticket_code}</td>
                              <td className="py-2 px-3">{t.passenger_name}</td>
                              <td className="py-2 px-3">{t.client?.name || '-'}</td>
                              <td className="py-2 px-3">{t.destination?.name || '-'}</td>
                              <td className="py-2 px-3">{t.airline?.name || '-'}</td>
                              <td className="py-2 px-3 text-right">�{parseFloat(t.fare).toFixed(2)}</td>
                              <td className="py-2 px-3 text-right text-green-600">�{parseFloat(t.tax).toFixed(2)}</td>
                              <td className="py-2 px-3 text-right font-bold">�{parseFloat(t.total).toFixed(2)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : <div className="text-center py-12 text-gray-500">Kliko "Kerko" per te shfaqur biletat</div>}
            </div>
          )}

          {/* ===== RAPORT BUXHETI ===== */}
          {activeTab === 'budget' && (
            <div>
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Nga Data</label>
                    <DatePicker selected={dateFrom} onChange={setDateFrom} dateFormat="dd/MM/yyyy" className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Deri</label>
                    <DatePicker selected={dateTo} onChange={setDateTo} dateFormat="dd/MM/yyyy" className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Lloji</label>
                    <select value={budgetType} onChange={(e) => setBudgetType(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm">
                      <option value="">Te gjitha</option>
                      <option value="client">Nga Klientet</option>
                      <option value="supplier">Per Furnitoret</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">{budgetType === 'supplier' ? 'Furnitori' : 'Klienti'}</label>
                    {budgetType === 'supplier' ? (
                      <Select value={selectedSupplier} onChange={setSelectedSupplier} options={supplierOptions} isClearable placeholder="Te gjithe..." className="text-sm" />
                    ) : (
                      <Select value={selectedClient} onChange={setSelectedClient} options={clientOptions} isClearable placeholder="Te gjithe..." className="text-sm" />
                    )}
                  </div>
                  <div className="flex items-end">
                    <button onClick={() => refetchBudget()} className="w-full px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700">Gjenero</button>
                  </div>
                </div>
              </div>

              {loadingBudget ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>
              ) : budgetReport ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
                      <p className="text-sm text-green-600 font-medium">Arketimet nga Klientet</p>
                      <p className="text-3xl font-bold text-green-700">�{(budgetReport.totals?.client || 0).toFixed(2)}</p>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg border-2 border-red-200">
                      <p className="text-sm text-red-600 font-medium">Pagesat per Furnitoret</p>
                      <p className="text-3xl font-bold text-red-700">�{(budgetReport.totals?.supplier || 0).toFixed(2)}</p>
                    </div>
                    <div className={`p-4 rounded-lg border-2 ${(budgetReport.totals?.balance || 0) >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'}`}>
                      <p className="text-sm font-medium">Bilanci</p>
                      <p className={`text-3xl font-bold ${(budgetReport.totals?.balance || 0) >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>�{(budgetReport.totals?.balance || 0).toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-700 text-white">
                        <tr>
                          <th className="text-left py-3 px-3">Data</th>
                          <th className="text-left py-3 px-3">Lloji</th>
                          <th className="text-left py-3 px-3">Emri</th>
                          <th className="text-left py-3 px-3">Llogaria</th>
                          <th className="text-left py-3 px-3">Shenim</th>
                          <th className="text-right py-3 px-3">Shuma</th>
                        </tr>
                      </thead>
                      <tbody>
                        {budgetReport.payments?.length === 0 ? (
                          <tr><td colSpan="6" className="text-center py-8 text-gray-500">Nuk ka pagesa</td></tr>
                        ) : (
                          budgetReport.payments?.map((p) => (
                            <tr key={p.id} className={`border-b ${p.type === 'client' ? 'bg-green-50' : 'bg-red-50'}`}>
                              <td className="py-2 px-3">{dayjs(p.payment_date).format('DD/MM/YYYY')}</td>
                              <td className="py-2 px-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${p.type === 'client' ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                                  {p.type === 'client' ? 'Nga Klient' : 'Per Furnitor'}
                                </span>
                              </td>
                              <td className="py-2 px-3 font-medium">{p.client?.name || p.supplier?.name || '-'}</td>
                              <td className="py-2 px-3">{p.bank_account?.name || '-'}</td>
                              <td className="py-2 px-3 text-gray-500">{p.notes || '-'}</td>
                              <td className={`py-2 px-3 text-right font-bold ${p.type === 'client' ? 'text-green-600' : 'text-red-600'}`}>�{parseFloat(p.amount).toFixed(2)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : <div className="text-center py-12 text-gray-500">Kliko "Gjenero" per te shfaqur pagesat</div>}
            </div>
          )}

          {/* ===== RAPORT KLIENTI ===== */}
          {activeTab === 'client' && (
            <div>
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Klienti</label>
                    <Select value={selectedClient} onChange={setSelectedClient} options={clientOptions} isClearable placeholder="Te gjithe..." className="text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Nga Data</label>
                    <DatePicker selected={dateFrom} onChange={setDateFrom} dateFormat="dd/MM/yyyy" className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Deri</label>
                    <DatePicker selected={dateTo} onChange={setDateTo} dateFormat="dd/MM/yyyy" className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div className="flex items-end">
                    <button onClick={() => refetchClient()} className="w-full px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700">Gjenero</button>
                  </div>
                </div>
              </div>

              {loadingClient ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>
              ) : clientReport ? (
                <>
                  <div className="flex gap-4 mb-4">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm">Bilete</span>
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded text-sm">Pagese</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-green-600 text-white">
                        <tr>
                          <th className="text-left py-3 px-3">Data</th>
                          <th className="text-left py-3 px-3">Klienti</th>
                          <th className="text-left py-3 px-3">Pasagjeri</th>
                          <th className="text-left py-3 px-3">Kodi</th>
                          <th className="text-left py-3 px-3">Destinacioni</th>
                          <th className="text-right py-3 px-3">Bilete</th>
                          <th className="text-right py-3 px-3">TSC</th>
                          <th className="text-right py-3 px-3">Pagese</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientReport.records?.length === 0 ? (
                          <tr><td colSpan="8" className="text-center py-8 text-gray-500">Nuk ka te dhena</td></tr>
                        ) : (
                          clientReport.records?.map((r, idx) => (
                            <tr key={idx} className={r.type === 'ticket' ? 'bg-blue-50' : 'bg-yellow-50'}>
                              <td className="py-2 px-3 border-b">{dayjs(r.date).format('DD/MM/YYYY')}</td>
                              <td className="py-2 px-3 border-b">{r.client_name || '-'}</td>
                              <td className="py-2 px-3 border-b">{r.passenger_name || '-'}</td>
                              <td className="py-2 px-3 border-b font-mono text-xs">{r.ticket_code || '-'}</td>
                              <td className="py-2 px-3 border-b">{r.destination || '-'}</td>
                              <td className="py-2 px-3 border-b text-right">{r.type === 'ticket' ? `�${parseFloat(r.total).toFixed(2)}` : ''}</td>
                              <td className="py-2 px-3 border-b text-right text-green-600">{r.type === 'ticket' ? `�${parseFloat(r.tax).toFixed(2)}` : ''}</td>
                              <td className="py-2 px-3 border-b text-right text-orange-600">{r.type === 'payment' ? `�${parseFloat(r.amount).toFixed(2)}` : ''}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-6 bg-gray-100 p-4 rounded-lg border-2 border-gray-300">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div><p className="text-sm text-gray-500">Total Biletave</p><p className="text-xl font-bold text-blue-600">�{(clientReport.totals?.tickets || 0).toFixed(2)}</p></div>
                      <div><p className="text-sm text-gray-500">Total Tarifa</p><p className="text-xl font-bold text-purple-600">�{(clientReport.totals?.fare || 0).toFixed(2)}</p></div>
                      <div><p className="text-sm text-gray-500">Total TSC</p><p className="text-xl font-bold text-green-600">�{(clientReport.totals?.tax || 0).toFixed(2)}</p></div>
                      <div><p className="text-sm text-gray-500">Total Pagesave</p><p className="text-xl font-bold text-orange-600">�{(clientReport.totals?.payments || 0).toFixed(2)}</p></div>
                      <div className="bg-white p-2 rounded-lg border-2 border-red-300"><p className="text-sm text-gray-500">Detyrim</p><p className={`text-2xl font-bold ${(clientReport.totals?.debt || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>�{(clientReport.totals?.debt || 0).toFixed(2)}</p></div>
                    </div>
                  </div>
                </>
              ) : <div className="text-center py-12 text-gray-500">Zgjidh filtrat dhe kliko "Gjenero"</div>}
            </div>
          )}

          {/* ===== RAPORT FURNITORI ===== */}
          {activeTab === 'supplier' && (
            <div>
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Furnitori</label>
                    <Select value={selectedSupplier} onChange={setSelectedSupplier} options={supplierOptions} isClearable placeholder="Te gjithe..." className="text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Nga Data</label>
                    <DatePicker selected={dateFrom} onChange={setDateFrom} dateFormat="dd/MM/yyyy" className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Deri</label>
                    <DatePicker selected={dateTo} onChange={setDateTo} dateFormat="dd/MM/yyyy" className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div className="flex items-end">
                    <button onClick={() => refetchSupplier()} className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700">Gjenero</button>
                  </div>
                </div>
              </div>

              {loadingSupplier ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>
              ) : supplierReport ? (
                <>
                  <div className="flex gap-4 mb-4">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm">Bilete</span>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm">Pagese</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-orange-600 text-white">
                        <tr>
                          <th className="text-left py-3 px-3">Data</th>
                          <th className="text-left py-3 px-3">Furnitori</th>
                          <th className="text-left py-3 px-3">Pasagjeri</th>
                          <th className="text-left py-3 px-3">Kodi</th>
                          <th className="text-left py-3 px-3">Destinacioni</th>
                          <th className="text-right py-3 px-3">Tarifa</th>
                          <th className="text-right py-3 px-3">Pagese</th>
                        </tr>
                      </thead>
                      <tbody>
                        {supplierReport.records?.length === 0 ? (
                          <tr><td colSpan="7" className="text-center py-8 text-gray-500">Nuk ka te dhena</td></tr>
                        ) : (
                          supplierReport.records?.map((r, idx) => (
                            <tr key={idx} className={r.type === 'ticket' ? 'bg-blue-50' : 'bg-green-50'}>
                              <td className="py-2 px-3 border-b">{dayjs(r.date).format('DD/MM/YYYY')}</td>
                              <td className="py-2 px-3 border-b">{r.supplier_name || '-'}</td>
                              <td className="py-2 px-3 border-b">{r.passenger_name || '-'}</td>
                              <td className="py-2 px-3 border-b font-mono text-xs">{r.ticket_code || '-'}</td>
                              <td className="py-2 px-3 border-b">{r.destination || '-'}</td>
                              <td className="py-2 px-3 border-b text-right text-blue-600">{r.type === 'ticket' ? `�${parseFloat(r.fare).toFixed(2)}` : ''}</td>
                              <td className="py-2 px-3 border-b text-right text-green-600">{r.type === 'payment' ? `�${parseFloat(r.amount).toFixed(2)}` : ''}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-6 bg-gray-100 p-4 rounded-lg border-2 border-gray-300">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div><p className="text-sm text-gray-500">Total Tarifave</p><p className="text-xl font-bold text-blue-600">�{(supplierReport.totals?.fare || 0).toFixed(2)}</p></div>
                      <div><p className="text-sm text-gray-500">Total Pagesave</p><p className="text-xl font-bold text-green-600">�{(supplierReport.totals?.payments || 0).toFixed(2)}</p></div>
                      <div className="bg-white p-2 rounded-lg border-2 border-orange-300"><p className="text-sm text-gray-500">Detyrim</p><p className={`text-2xl font-bold ${(supplierReport.totals?.debt || 0) > 0 ? 'text-orange-600' : 'text-green-600'}`}>�{(supplierReport.totals?.debt || 0).toFixed(2)}</p></div>
                    </div>
                  </div>
                </>
              ) : <div className="text-center py-12 text-gray-500">Zgjidh filtrat dhe kliko "Gjenero"</div>}
            </div>
          )}

          {/* ===== RAPORT KOMPANIE ===== */}
          {activeTab === 'airline' && (
            <div>
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Kompania</label>
                    <Select value={selectedAirline} onChange={setSelectedAirline} options={airlineOptions} isClearable placeholder="Te gjitha..." className="text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Nga Data</label>
                    <DatePicker selected={dateFrom} onChange={setDateFrom} dateFormat="dd/MM/yyyy" className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Deri</label>
                    <DatePicker selected={dateTo} onChange={setDateTo} dateFormat="dd/MM/yyyy" className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div className="flex items-end">
                    <button onClick={() => refetchAirline()} className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700">Gjenero</button>
                  </div>
                </div>
              </div>

              {loadingAirline ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>
              ) : airlineReport ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-indigo-600 text-white">
                        <tr>
                          <th className="text-left py-3 px-3">Data</th>
                          <th className="text-left py-3 px-3">Kompania</th>
                          <th className="text-left py-3 px-3">Pasagjeri</th>
                          <th className="text-left py-3 px-3">Kodi</th>
                          <th className="text-left py-3 px-3">Destinacioni</th>
                          <th className="text-left py-3 px-3">Klienti</th>
                          <th className="text-right py-3 px-3">Tarifa</th>
                          <th className="text-right py-3 px-3">TSC</th>
                          <th className="text-right py-3 px-3">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {airlineReport.tickets?.length === 0 ? (
                          <tr><td colSpan="9" className="text-center py-8 text-gray-500">Nuk ka te dhena</td></tr>
                        ) : (
                          airlineReport.tickets?.map((t, idx) => (
                            <tr key={idx} className="hover:bg-gray-50 border-b">
                              <td className="py-2 px-3">{dayjs(t.ticket_date).format('DD/MM/YYYY')}</td>
                              <td className="py-2 px-3 font-medium">{t.airline_name || '-'}</td>
                              <td className="py-2 px-3">{t.passenger_name}</td>
                              <td className="py-2 px-3 font-mono text-xs">{t.ticket_code}</td>
                              <td className="py-2 px-3">{t.destination_name || '-'}</td>
                              <td className="py-2 px-3">{t.client_name || '-'}</td>
                              <td className="py-2 px-3 text-right">�{parseFloat(t.fare).toFixed(2)}</td>
                              <td className="py-2 px-3 text-right text-green-600">�{parseFloat(t.tax).toFixed(2)}</td>
                              <td className="py-2 px-3 text-right font-bold">�{parseFloat(t.total).toFixed(2)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-6 bg-gray-100 p-4 rounded-lg border-2 border-gray-300">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div><p className="text-sm text-gray-500">Bileta</p><p className="text-xl font-bold text-gray-700">{airlineReport.totals?.count || 0}</p></div>
                      <div><p className="text-sm text-gray-500">Total Tarifa</p><p className="text-xl font-bold text-blue-600">�{(airlineReport.totals?.fare || 0).toFixed(2)}</p></div>
                      <div><p className="text-sm text-gray-500">Total TSC</p><p className="text-xl font-bold text-green-600">�{(airlineReport.totals?.tax || 0).toFixed(2)}</p></div>
                      <div><p className="text-sm text-gray-500">Total Shitje</p><p className="text-xl font-bold text-indigo-600">�{(airlineReport.totals?.total || 0).toFixed(2)}</p></div>
                    </div>
                  </div>
                </>
              ) : <div className="text-center py-12 text-gray-500">Zgjidh filtrat dhe kliko "Gjenero"</div>}
            </div>
          )}

          {/* ===== RAPORT FITIMI ===== */}
          {activeTab === 'profit' && (
            <div>
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Nga Data</label>
                    <DatePicker selected={dateFrom} onChange={setDateFrom} dateFormat="dd/MM/yyyy" className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Deri</label>
                    <DatePicker selected={dateTo} onChange={setDateTo} dateFormat="dd/MM/yyyy" className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div className="flex gap-2 flex-wrap items-end">
                    {[
                      { label: 'Sot', from: new Date(), to: new Date() },
                      { label: 'Kete Muaj', from: dayjs().startOf('month').toDate(), to: new Date() },
                      { label: 'Kete Vit', from: dayjs().startOf('year').toDate(), to: new Date() },
                    ].map((r) => (
                      <button key={r.label} onClick={() => { setDateFrom(r.from); setDateTo(r.to); }} className="px-3 py-2 text-xs font-medium bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">{r.label}</button>
                    ))}
                  </div>
                </div>
              </div>

              {loadingProfit ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>
              ) : profitReport ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-xl">
                    <p className="text-blue-100 text-sm">Shitje Totale</p>
                    <p className="text-3xl font-bold mt-2">�{(profitReport.total_sales || 0).toFixed(2)}</p>
                    <p className="text-blue-200 text-xs mt-1">{profitReport.ticket_count || 0} bileta</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-xl">
                    <p className="text-green-100 text-sm">Fitim Bruto (TSC)</p>
                    <p className="text-3xl font-bold mt-2">�{(profitReport.ticket_profit || 0).toFixed(2)}</p>
                  </div>
                  <div className="bg-gradient-to-br from-red-500 to-red-600 text-white p-6 rounded-xl">
                    <p className="text-red-100 text-sm">Shpenzime</p>
                    <p className="text-3xl font-bold mt-2">�{(profitReport.expenses || 0).toFixed(2)}</p>
                  </div>
                  <div className={`bg-gradient-to-br ${(profitReport.net_profit || 0) >= 0 ? 'from-emerald-500 to-emerald-600' : 'from-orange-500 to-orange-600'} text-white p-6 rounded-xl`}>
                    <p className="text-white/80 text-sm">Fitim Neto</p>
                    <p className="text-3xl font-bold mt-2">�{(profitReport.net_profit || 0).toFixed(2)}</p>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}